import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Dna, Download, Upload, ArrowRight, ExternalLink, ShieldCheck, HeartHandshake } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const DOWNLOAD_STEPS = [
  {
    name: "Ancestry.com",
    link: "https://support.ancestry.com/s/article/Requesting-a-Download-of-Your-Account-Data?language=en_US",
    steps: [
      "If prompted, sign in to your Ancestry.com account.",
      "In the \"Your DNA tests\" section, select a name to download the DNA data for that test.",
      "Check the box confirming you understand the downloaded copy won't be protected by Ancestry's security measures, then select Confirm. This triggers a confirmation email (may take up to 24 hours; the link expires after 7 days).",
      "Enter your password and hit Continue.",
      "When the email arrives, open it and click Confirm Data Download.",
      "On the Download DNA Data page, click Download DNA Data (sign in again if prompted).",
      "The file downloads as AncestryDNA.txt — check your Downloads folder or Desktop if you can't find it.",
    ],
  },
  {
    name: "23andMe.com",
    link: "https://customercare.23andme.com/hc/en-us/articles/212196868-Accessing-Your-Raw-Genetic-Data",
    steps: [
      "Navigate directly to you.23andme.com/tools/data/ — or —",
      "Click your profile name (top right) → Resources → Browse Raw Genotyping Data → Download.",
      "Or visit Account Settings and click View under 23andMe Data.",
      "Confirm the date of birth associated with the profile, read the important considerations, check the box, and select Submit Request.",
      "You'll get an email within 48 hours once your data is ready. Verify the profile birthdate again to download.",
    ],
  },
];

const UPLOAD_STEPS = [
  {
    name: "Family Tree DNA (FTDNA)",
    link: "https://help.familytreedna.com/hc/en-us/articles/4402392808463-Autosomal-DNA-Transfers-Guide",
    steps: [
      "Create a FREE account at FTDNA.",
      "You can upload Ancestry.com & MyHeritage DNA files.",
      "Navigate to the \"Upload DNA\" tab at the top of the window.",
      "Choose Autosomal DNA from the drop-down menu.",
      "On the autosomal DNA transfer page, enter the first name, last name, email, and sex of the person whose data you're transferring.",
      "Click Join Today — you'll be directed to the upload page.",
      "Choose the platform your DNA originated from (Ancestry.com or MyHeritage).",
      "Drag and drop the .zip file you downloaded (or browse for it), then click Submit.",
      "You'll receive an email with your kit number and temporary password.",
    ],
  },
  {
    name: "GEDmatch.com",
    link: "https://www.gedmatch.com/how-it-works/",
    steps: [
      "Register for an account at GEDmatch.com.",
      "Log in to GEDmatch.",
      "Click Upload DNA, then the \"Choose File\" button.",
      "Provide as much information as you can in the form.",
      "Fill in your consent.",
      "Click Upload and wait until you see the word \"Finish\".",
      "Wait for processing to complete.",
    ],
  },
];

function StepCard({ item, icon: Icon, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className="card-surface rounded-md p-6"
      data-testid={`dna-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-sm bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-gold" />
        </div>
        <h3 className="font-display text-xl font-semibold text-white">{item.name}</h3>
      </div>
      <ol className="space-y-3">
        {item.steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-white/70 leading-relaxed">
            <span className="shrink-0 w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs font-mono text-gold">{i + 1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      <a href={item.link} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:text-gold-hover transition-colors">
        Official instructions <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </motion.div>
  );
}

export default function MoveYourDnaPage() {
  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet>
        <title>Move Your DNA — Help Identify Unknown Human Remains | Help Solve The Case™</title>
        <meta name="description" content="Move your DNA to GEDmatch and FamilyTreeDNA to help identify unknown human remains through investigative genetic genealogy. Step-by-step instructions to download and upload your genome." />
        <link rel="canonical" href="https://helpsolvethecase.com/move-your-dna" />
        <meta property="og:title" content="Move Your DNA — Help Identify Unknown Human Remains" />
        <meta property="og:description" content="Step-by-step: download your DNA and upload it to GEDmatch & FTDNA to help give the unnamed their names back." />
      </Helmet>
      <Navbar />

      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.08] via-transparent to-transparent" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16">
          <div className="inline-flex items-center gap-2 label-mono mb-5 px-3 py-1.5 border border-gold/30 rounded-sm bg-gold/5">
            <Dna className="w-3.5 h-3.5 text-gold" /> Investigative Genetic Genealogy
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
            Move your DNA. <span className="text-gold">Give them their names back.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-2xl">
            Thank you for being part of the solution. By moving your DNA to GEDmatch and/or FamilyTreeDNA, you dramatically increase the odds of identifying unknown human remains — and putting these souls to rest properly.
          </p>
          <div className="mt-8 card-surface rounded-md p-5 text-sm text-white/70 leading-relaxed max-w-2xl">
            Your DNA isn't really stored "inside" the testing companies — it's a computer file called a <span className="text-white font-semibold">genome</span>, delivered as a compressed <span className="font-mono text-gold">.zip</span> file. First you <span className="text-white font-semibold">download</span> your genome from your testing company (Ancestry, 23andMe, MyHeritage, etc.), then you <span className="text-white font-semibold">upload</span> it to GEDmatch and/or FTDNA — ideally both.
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 space-y-16">
        <section>
          <div className="flex items-center gap-2 mb-6">
            <Download className="w-4 h-4 text-gold" />
            <h2 className="label-mono">Step 1 · Download your DNA</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {DOWNLOAD_STEPS.map((item, i) => <StepCard key={item.name} item={item} icon={Download} delay={i * 0.08} />)}
          </div>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-6">
            <Upload className="w-4 h-4 text-gold" />
            <h2 className="label-mono">Step 2 · Upload your DNA</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {UPLOAD_STEPS.map((item, i) => <StepCard key={item.name} item={item} icon={Upload} delay={i * 0.08} />)}
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-md border border-gold/30 bg-gold/5 p-5">
            <ShieldCheck className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <p className="text-sm text-white/70 leading-relaxed">
              <span className="text-white font-semibold">Important:</span> For your data to be available to law enforcement, you must <span className="text-white font-semibold">voluntarily consent</span> and "opt in" to law enforcement matching on both GEDmatch and FTDNA. It's your choice — and it's what makes identifications possible.
            </p>
          </div>
        </section>

        <section className="text-center">
          <HeartHandshake className="w-8 h-8 text-gold mx-auto mb-4" />
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white">Every genome moved is a chance at a name.</h2>
          <p className="mt-3 text-white/50 max-w-xl mx-auto">Thank you for helping bring answers to families who have waited far too long.</p>
          <a href="https://www.gedmatch.com/how-it-works/" target="_blank" rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
            Start with GEDmatch <ArrowRight className="w-4 h-4" />
          </a>
        </section>
      </div>

      <Footer />
    </div>
  );
}
